import React, { useEffect, useState } from 'react';
import { X, RefreshCw, Printer, Calendar, Filter, TrendingUp, TrendingDown, Loader2 } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { getDaybookForPayments } from '../../../../redux/actions/daybook';
import { getVoucher, updateClearingDate, updateVoucher } from '../../../../redux/actions/voucher';
import { toast } from 'react-toastify';
import { useUser } from '../../../../contexts';
import { DAYBOOK_TYPE } from 'constants';
import { renderDynamicDropdown } from '../../../../components/UIcomponents/DynamicDropdown';
import { useColorMode } from '@chakra-ui/react';

export default function BankReconciliation({ isRenumberingTransection, onClose, handleRenumberingTransection }) {
   const { colorMode } = useColorMode();
   const isDark = colorMode === 'dark';

   const daybookTypeData = useSelector((state) => state.daybook?.listPayment || null);
   const [errors, setErrors] = useState({});
   const [isLoading, setIsLoading] = useState(false);
   const { user } = useUser();
   const [selectedDaybook, setSelectedDaybook] = useState('');
   const [filters, setFilters] = useState({
      fromDate: '2025-04-01',
      toDate: '2026-03-31',
      type: 'Both',
      clearStatus: 'Both',
      slipWiseTotal: true,
      bankBalance: 0,
   });
   const voucher = useSelector((state) => state.voucher?.list || []);
   const [transactionsData, setTransactionsData] = useState(voucher);
   const [focusedRowIndex, setFocusedRowIndex] = useState(null);
   const [summary, setSummary] = useState({
      balanceAsPerBooks: 0,
      unclearedDeposits: 0,
      unclearedWithdrawals: 0,
      balanceAsPerBank: 0,
      difference: 0,
   });
   const dispatch = useDispatch();

   useEffect(() => {
      if (user?.loginhistory?.companyId && isRenumberingTransection) {
         dispatch(getDaybookForPayments({ type: DAYBOOK_TYPE.BANK, companyId: user?.loginhistory?.companyId }));
      }
   }, [dispatch, user, user?.loginhistory?.companyId, isRenumberingTransection]);

   useEffect(() => {
      if (voucher && voucher.length > 0) {
         setTransactionsData(voucher);
      }
   }, [voucher]);

   useEffect(() => {
      calculateSummary();
   }, [transactionsData, filters.bankBalance]);

   const calculateSummary = () => {
      const unclearedDeposits = transactionsData
         .filter((tx) => tx.amount && !tx.clearingDate)
         .reduce((sum, tx) => sum + (tx.amount || 0), 0);

      const unclearedWithdrawals = transactionsData
         .filter((tx) => tx.amount && !tx.clearingDate)
         .reduce((sum, tx) => sum + (tx.amount || 0), 0);

      const balanceAsPerBooks = transactionsData.reduce((balance, tx) => {
         return balance + (tx.amount || 0);
      }, 0);

      const bankBalance = parseFloat(filters.bankBalance) || 0;
      const difference = bankBalance - (balanceAsPerBooks + unclearedDeposits - unclearedWithdrawals);

      setSummary({
         balanceAsPerBooks,
         unclearedDeposits,
         unclearedWithdrawals,
         balanceAsPerBank: bankBalance,
         difference,
      });
   };

   const handleFilterChange = (field, value) => {
      setFilters((prev) => ({
         ...prev,
         [field]: value,
      }));
   };

   const handleDaybookChange = (value) => {
      setSelectedDaybook(value);
   };

   const loadTransactions = () => {
      setIsLoading(true);

      const params = {
         daybookId: selectedDaybook || undefined,
         type: DAYBOOK_TYPE.BANK,
         ...(filters.type !== 'Both' && { txnType: filters.type }),
      };

      dispatch(getVoucher(params))
         .then(() => {
            setIsLoading(false);
            if (transactionsData.length > 0) {
               setFocusedRowIndex(0);
            }
         })
         .catch((error) => {
            console.error('Error loading transactions:', error);
            setIsLoading(false);
            toast.error('Failed to load transactions');
         });
   };

   const formatDateForDisplay = (dateString) => {
      if (!dateString) return '';
      try {
         const date = new Date(dateString);
         if (isNaN(date.getTime())) return '';
         const day = String(date.getDate()).padStart(2, '0');
         const month = String(date.getMonth() + 1).padStart(2, '0');
         const year = date.getFullYear();
         return `${day}/${month}/${year}`;
      } catch (error) {
         console.error('Date formatting error:', error);
         return '';
      }
   };

   const formatDateForInput = (dateString) => {
      if (!dateString) return '';
      try {
         if (dateString.includes('/')) {
            const [day, month, year] = dateString.split('/');
            return `${year}-${month}-${day}`;
         }
         const date = new Date(dateString);
         if (isNaN(date.getTime())) return '';
         const year = date.getFullYear();
         const month = String(date.getMonth() + 1).padStart(2, '0');
         const day = String(date.getDate()).padStart(2, '0');
         return `${year}-${month}-${day}`;
      } catch (error) {
         console.error('Date formatting error:', error);
         return '';
      }
   };

   const addOneDayToDate = (dateString) => {
      if (!dateString) return '';
      try {
         const date = new Date(dateString);
         date.setDate(date.getDate() + 1);
         const year = date.getFullYear();
         const month = String(date.getMonth() + 1).padStart(2, '0');
         const day = String(date.getDate()).padStart(2, '0');
         return `${year}-${month}-${day}`;
      } catch (error) {
         return dateString;
      }
   };

   const setNextDayAfterChequeDate = (rowIndex) => {
      const transaction = transactionsData[rowIndex];
      if (transaction.chequeDate) {
         let formattedDate;
         if (transaction.chequeDate.includes('/')) {
            const parts = transaction.chequeDate.split('/');
            formattedDate = `${parts[2]}-${parts[1]}-${parts[0]}`;
         } else {
            formattedDate = transaction.chequeDate;
         }
         const nextDay = addOneDayToDate(formattedDate);
         const updatedTransactions = [...transactionsData];
         updatedTransactions[rowIndex] = {
            ...transaction,
            clearingDate: nextDay,
         };
         setTransactionsData(updatedTransactions);
         setFocusedRowIndex(rowIndex);
         setTimeout(() => {
            const inputElement = document.getElementById(`clearing-date-${rowIndex}`);
            if (inputElement) {
               inputElement.focus();
            }
         }, 0);
      }
   };

   const handleClearingDateChange = (rowIndex, value) => {
      const updatedTransactions = [...transactionsData];
      updatedTransactions[rowIndex] = {
         ...updatedTransactions[rowIndex],
         clearingDate: value,
      };
      setTransactionsData(updatedTransactions);
   };

   const handleClearingDateKeyDown = (e, rowIndex) => {
      if (e.key === 'Delete') {
         e.preventDefault();
         const transaction = transactionsData[rowIndex];
         if (transaction.clearingDate) {
            const nextDay = addOneDayToDate(transaction.clearingDate);
            const updatedTransactions = [...transactionsData];
            updatedTransactions[rowIndex] = {
               ...transaction,
               clearingDate: null,
            };
            setTransactionsData(updatedTransactions);
            dispatch(updateClearingDate({ voucherId: transaction.id, clearingDate: null }))
               .then((res) => {
                  const updated = res?.data?.data;
                  if (updated) {
                     const updatedTransactions = transactionsData.map((item) =>
                        item.id === updated.id ? { ...item, ...updated } : item,
                     );
                     setTransactionsData(updatedTransactions);
                  }
                  if (rowIndex < transactionsData.length - 1) {
                     setFocusedRowIndex(rowIndex + 1);
                     setTimeout(() => {
                        const nextInput = document.getElementById(`clearing-date-${rowIndex + 1}`);
                        if (nextInput) {
                           nextInput.focus();
                        }
                     }, 0);
                  }
                  loadTransactions();
               })
               .catch((error) => {
                  toast.error('Failed to update clearing date');
                  console.error('Error updating voucher:', error);
               });
         } else if (transaction.chequeDate) {
            setNextDayAfterChequeDate(rowIndex);
         }
      }
      if (e.key === '+') {
         e.preventDefault();
         const transaction = transactionsData[rowIndex];
         if (transaction.clearingDate) {
            const nextDay = addOneDayToDate(transaction.clearingDate);
            const updatedTransactions = [...transactionsData];
            updatedTransactions[rowIndex] = {
               ...transaction,
               clearingDate: nextDay,
            };
            setTransactionsData(updatedTransactions);
         } else if (transaction.chequeDate) {
            setNextDayAfterChequeDate(rowIndex);
         }
      } else if (e.key === 'Enter' || e.key === 'Tab') {
         e.preventDefault();
         const transaction = transactionsData[rowIndex];
         dispatch(
            updateClearingDate({
               voucherId: transaction.id,
               clearingDate: transaction.clearingDate,
            }),
         )
            .then((res) => {
               const updated = res?.data?.data;
               if (updated) {
                  const updatedTransactions = transactionsData.map((item) =>
                     item.id === updated.id ? { ...item, ...updated } : item,
                  );
                  setTransactionsData(updatedTransactions);
               }
               if (rowIndex < transactionsData.length - 1) {
                  setFocusedRowIndex(rowIndex + 1);
                  setTimeout(() => {
                     const nextInput = document.getElementById(`clearing-date-${rowIndex + 1}`);
                     if (nextInput) {
                        nextInput.focus();
                     }
                  }, 0);
               }
               loadTransactions();
            })
            .catch((error) => {
               toast.error('Failed to update clearing date');
               console.error('Error updating voucher:', error);
            });
      }
   };

   const handleAddClearingDateClick = (rowIndex) => {
      setNextDayAfterChequeDate(rowIndex);
   };

   const handleRemoveClearingDate = (rowIndex) => {
      const updatedTransactions = [...transactionsData];
      updatedTransactions[rowIndex] = {
         ...updatedTransactions[rowIndex],
         clearingDate: null,
      };
      setTransactionsData(updatedTransactions);
   };

   useEffect(() => {
      const handleEscape = (e) => {
         if (e.key === 'Escape' && isRenumberingTransection) {
            onClose();
         }
      };

      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
   }, [isRenumberingTransection, onClose]);

   if (!isRenumberingTransection) return null;

   return (
      <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/60 ">
         <div
            className={`w-[75vw] h-[92vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden ${
               isDark ? 'bg-gray-900' : 'bg-white'
            }`}>
            {/* Header Section */}
            <div
               className={`px-6 py-5 border-b-2 ${
                  isDark
                     ? 'bg-gradient-to-r from-slate-800 to-slate-900 border-slate-700'
                     : 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200'
               }`}>
               <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                     <div className={`p-3 rounded-xl ${isDark ? 'bg-blue-600/20' : 'bg-blue-600/10'}`}>
                        <TrendingUp className={`w-7 h-7 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                     </div>
                     <div>
                        <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                           Bank Reconciliation
                        </h2>
                        <p className={`text-sm mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                           Manage and track your bank transactions
                        </p>
                     </div>
                  </div>

                  <button
                     onClick={onClose}
                     className={`p-2.5 rounded-xl transition-all duration-200 ${
                        isDark
                           ? 'hover:bg-red-600/20 text-gray-400 hover:text-red-400'
                           : 'hover:bg-red-50 text-gray-600 hover:text-red-600'
                     }`}>
                     <X className="w-6 h-6" />
                  </button>
               </div>

               {/* Bank Account Selection */}
               <div className="mt-4">
                  {renderDynamicDropdown(
                     'daybookId',
                     'Bank Account',
                     daybookTypeData || [],
                     {
                        value: selectedDaybook,
                        onChange: handleDaybookChange,
                        openfrom: 'daybook',
                        placeholder: 'Select Bank Account',
                        color: isDark ? 'white' : 'black',
                        bg: isDark ? '#1e293b' : '#f1f5f9',
                        borderRadius: '12px',
                        fontSize: '16px',
                        height: '48px',
                        width: '280px',
                        showCustomNoOptions: true,
                     },
                     errors,
                     isDark ? 'white' : 'black',
                     false,
                     'Select Bank Account',
                  )}
               </div>
            </div>

            {/* Filters Section */}
            <div
               className={`px-6 py-4 border-b ${
                  isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-50 border-gray-200'
               }`}>
               <div className="flex flex-wrap gap-4 items-end">
                  <div className="flex-1 min-w-[140px]">
                     <label
                        className={`block text-xs font-semibold mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        <Calendar className="w-3.5 h-3.5 inline mr-1" />
                        From Date
                     </label>
                     <input
                        type="date"
                        value={filters.fromDate}
                        onChange={(e) => handleFilterChange('fromDate', e.target.value)}
                        className={`w-full px-3 py-2.5 rounded-lg border-2 transition-all duration-200 focus:outline-none focus:ring-2 ${
                           isDark
                              ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500 focus:ring-blue-500/20'
                              : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500/20'
                        }`}
                     />
                  </div>

                  <div className="flex-1 min-w-[140px]">
                     <label
                        className={`block text-xs font-semibold mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        <Calendar className="w-3.5 h-3.5 inline mr-1" />
                        To Date
                     </label>
                     <input
                        type="date"
                        value={filters.toDate}
                        onChange={(e) => handleFilterChange('toDate', e.target.value)}
                        className={`w-full px-3 py-2.5 rounded-lg border-2 transition-all duration-200 focus:outline-none focus:ring-2 ${
                           isDark
                              ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500 focus:ring-blue-500/20'
                              : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500/20'
                        }`}
                     />
                  </div>

                  <div className="flex-1 min-w-[140px]">
                     <label
                        className={`block text-xs font-semibold mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        <Filter className="w-3.5 h-3.5 inline mr-1" />
                        Type
                     </label>
                     <select
                        value={filters.type}
                        onChange={(e) => handleFilterChange('type', e.target.value)}
                        className={`w-full px-3 py-2.5 rounded-lg border-2 transition-all duration-200 focus:outline-none focus:ring-2 ${
                           isDark
                              ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500 focus:ring-blue-500/20'
                              : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500/20'
                        }`}>
                        <option>Both</option>
                        <option value={0}>Receipt</option>
                        <option value={1}>Payment</option>
                     </select>
                  </div>

                  <div className="flex-1 min-w-[140px]">
                     <label
                        className={`block text-xs font-semibold mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        <Filter className="w-3.5 h-3.5 inline mr-1" />
                        Clear Status
                     </label>
                     <select
                        value={filters.clearStatus}
                        onChange={(e) => handleFilterChange('clearStatus', e.target.value)}
                        className={`w-full px-3 py-2.5 rounded-lg border-2 transition-all duration-200 focus:outline-none focus:ring-2 ${
                           isDark
                              ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500 focus:ring-blue-500/20'
                              : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500/20'
                        }`}>
                        <option>Both</option>
                        <option>Cleared</option>
                        <option>Uncleared</option>
                     </select>
                  </div>

                  <div className="flex-1 min-w-[160px]">
                     <label
                        className={`block text-xs font-semibold mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        Balance as per Bank
                     </label>
                     <input
                        type="number"
                        placeholder="0.00"
                        value={filters.bankBalance}
                        onChange={(e) => handleFilterChange('bankBalance', e.target.value)}
                        className={`w-full px-3 py-2.5 rounded-lg border-2 transition-all duration-200 focus:outline-none focus:ring-2 ${
                           isDark
                              ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500 focus:ring-blue-500/20 placeholder-gray-500'
                              : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500/20 placeholder-gray-400'
                        }`}
                     />
                  </div>

                  <button
                     onClick={loadTransactions}
                     disabled={isLoading}
                     className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
                     {isLoading ? (
                        <>
                           <Loader2 className="w-4 h-4 animate-spin" />
                           Loading...
                        </>
                     ) : (
                        <>
                           <RefreshCw className="w-4 h-4" />
                           Load
                        </>
                     )}
                  </button>
               </div>
            </div>

            {/* Table Section */}
            <div className="flex-1 overflow-auto px-6 py-4">
               <div className={`rounded-xl overflow-hidden border-2 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                  <table className="w-full">
                     <thead
                        className={`sticky top-0 z-10 ${
                           isDark
                              ? 'bg-gradient-to-r from-gray-800 to-gray-900'
                              : 'bg-gradient-to-r from-gray-100 to-gray-200'
                        }`}>
                        <tr>
                           <th
                              className={`px-4 py-4 text-left text-xs font-bold uppercase tracking-wider border-b-2 ${
                                 isDark ? 'text-gray-300 border-gray-700' : 'text-gray-700 border-gray-300'
                              }`}>
                              Date
                           </th>
                           <th
                              className={`px-4 py-4 text-left text-xs font-bold uppercase tracking-wider border-b-2 ${
                                 isDark ? 'text-gray-300 border-gray-700' : 'text-gray-700 border-gray-300'
                              }`}>
                              Document
                           </th>
                           <th
                              className={`px-4 py-4 text-left text-xs font-bold uppercase tracking-wider border-b-2 ${
                                 isDark ? 'text-gray-300 border-gray-700' : 'text-gray-700 border-gray-300'
                              }`}>
                              Account Name
                           </th>
                           <th
                              className={`px-4 py-4 text-left text-xs font-bold uppercase tracking-wider border-b-2 ${
                                 isDark ? 'text-gray-300 border-gray-700' : 'text-gray-700 border-gray-300'
                              }`}>
                              Cheque #
                           </th>
                           <th
                              className={`px-4 py-4 text-left text-xs font-bold uppercase tracking-wider border-b-2 ${
                                 isDark ? 'text-gray-300 border-gray-700' : 'text-gray-700 border-gray-300'
                              }`}>
                              Cheque Date
                           </th>
                           <th
                              className={`px-4 py-4 text-right text-xs font-bold uppercase tracking-wider border-b-2 ${
                                 isDark ? 'text-green-400 border-gray-700' : 'text-green-700 border-gray-300'
                              }`}>
                              <TrendingUp className="w-4 h-4 inline mr-1" />
                              Deposits
                           </th>
                           <th
                              className={`px-4 py-4 text-right text-xs font-bold uppercase tracking-wider border-b-2 ${
                                 isDark ? 'text-red-400 border-gray-700' : 'text-red-700 border-gray-300'
                              }`}>
                              <TrendingDown className="w-4 h-4 inline mr-1" />
                              Withdrawals
                           </th>
                           <th
                              className={`px-4 py-4 text-left text-xs font-bold uppercase tracking-wider border-b-2 ${
                                 isDark ? 'text-gray-300 border-gray-700' : 'text-gray-700 border-gray-300'
                              }`}>
                              Clearing Date
                           </th>
                        </tr>
                     </thead>
                     <tbody className={isDark ? 'bg-gray-900' : 'bg-white'}>
                        {transactionsData?.map((tx, index) => (
                           <tr
                              key={tx.id || index}
                              className={`border-b transition-all duration-200 ${
                                 focusedRowIndex === index
                                    ? isDark
                                       ? 'bg-blue-900/30 border-blue-700'
                                       : 'bg-blue-50 border-blue-300'
                                    : isDark
                                    ? 'border-gray-800 hover:bg-gray-800/50'
                                    : 'border-gray-100 hover:bg-gray-50'
                              }`}>
                              <td className={`px-4 py-3 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                 <div className="font-medium">{tx.txnDate}</div>
                                 <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{tx.day}</div>
                              </td>
                              <td
                                 className={`px-4 py-3 text-sm font-medium ${
                                    isDark ? 'text-gray-200' : 'text-gray-800'
                                 }`}>
                                 {tx.docNumber}
                              </td>
                              <td className={`px-4 py-3 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                 {tx.accountName}
                              </td>
                              <td
                                 className={`px-4 py-3 text-sm font-mono ${
                                    isDark ? 'text-gray-400' : 'text-gray-600'
                                 }`}>
                                 {tx.chequeNo}
                              </td>
                              <td className={`px-4 py-3 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                 {new Date(tx.chequeDate).toLocaleDateString('en-GB')}
                              </td>
                              <td
                                 className={`px-4 py-3 text-sm text-right font-semibold ${
                                    tx.amount > 0 ? (isDark ? 'text-green-400' : 'text-green-700') : ''
                                 }`}>
                                 {tx.amount && tx.amount > 0
                                    ? tx.amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })
                                    : ''}
                              </td>
                              <td
                                 className={`px-4 py-3 text-sm text-right font-semibold ${
                                    tx.amount < 0 ? (isDark ? 'text-red-400' : 'text-red-700') : ''
                                 }`}>
                                 {tx.amount && tx.amount < 0
                                    ? Math.abs(tx.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })
                                    : ''}
                              </td>
                              <td className="px-4 py-3">
                                 <input
                                    id={`clearing-date-${index}`}
                                    type="date"
                                    value={
                                       tx.clearingDate ? formatDateForInput(formatDateForDisplay(tx.clearingDate)) : ''
                                    }
                                    onChange={(e) => handleClearingDateChange(index, e.target.value)}
                                    onKeyDown={(e) => handleClearingDateKeyDown(e, index)}
                                    autoFocus={focusedRowIndex === index}
                                    className={`w-full px-2.5 py-1.5 rounded-lg border-2 text-sm transition-all duration-200 focus:outline-none focus:ring-2 ${
                                       isDark
                                          ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500 focus:ring-blue-500/20'
                                          : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500/20'
                                    }`}
                                 />
                              </td>
                           </tr>
                        ))}
                     </tbody>
                  </table>
               </div>
            </div>

            {/* Footer Section */}
            <div
               className={`px-6 py-4 border-t-2 ${
                  isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'
               }`}>
               <div className="flex items-center justify-between gap-4">
                  <div className="flex-1 space-y-1">
                     <p className={`text-xs font-medium ${isDark ? 'text-red-400' : 'text-red-600'}`}>
                        <span className="font-bold">+</span> Fill Clear Date (one day after cheque date) &{' '}
                        <span className="font-bold">-</span> Remove Clear Date
                     </p>
                     <p className={`text-xs font-medium ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                        Press the <span className="font-bold">+</span> key in the clearing date field to add one day to
                        the date
                     </p>
                  </div>

                  <div className="flex items-center gap-3">
                     <button
                        onClick={loadTransactions}
                        disabled={isLoading}
                        className={`px-5 py-2.5 rounded-lg font-semibold transition-all duration-200 shadow-md hover:shadow-lg flex items-center gap-2 ${
                           isDark
                              ? 'bg-blue-600 hover:bg-blue-700 text-white'
                              : 'bg-blue-600 hover:bg-blue-700 text-white'
                        } disabled:opacity-50 disabled:cursor-not-allowed`}>
                        <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                        Refresh
                     </button>

                     <button
                        className={`px-5 py-2.5 rounded-lg font-semibold transition-all duration-200 shadow-md hover:shadow-lg flex items-center gap-2 ${
                           isDark
                              ? 'bg-green-600 hover:bg-green-700 text-white'
                              : 'bg-green-600 hover:bg-green-700 text-white'
                        }`}>
                        <Printer className="w-4 h-4" />
                        Print
                     </button>

                     <button
                        onClick={onClose}
                        className={`px-5 py-2.5 rounded-lg font-semibold transition-all duration-200 shadow-md hover:shadow-lg flex items-center gap-2 ${
                           isDark ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-red-600 hover:bg-red-700 text-white'
                        }`}>
                        <X className="w-4 h-4" />
                        Close
                     </button>
                  </div>
               </div>
            </div>
         </div>
      </div>
   );
}
